import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { PricePlanTier } from '@utils/types/back-office/price-plans-bo.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { AxiosError } from 'axios';

interface UpdatePricePlanBoPayload {
  name: string;
  price_per_unit?: string;
  tiers: PricePlanTier[];
}

export const useUpdatePricePlanBo = (pricePlanId: number) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updatePricePlan = async (data: UpdatePricePlanBoPayload) => {
    const response = await Axios.patch(
      `${POST_URL(pricePlanId.toString()).pricePlansBo}`,
      data
    );
    return response.data;
  };

  return useMutation<void, AxiosError<BaseError>, UpdatePricePlanBoPayload>(
    updatePricePlan,
    {
      onSuccess: () => {
        toast.success(t('price-plan-updated-successfully'));
        queryClient.invalidateQueries(['pricePlansBo']);
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error.response?.data;
        let errorMessage = t('failed-to-update-price-plan');
        if (data?.detail && Array.isArray(data.detail)) {
          errorMessage = data.detail
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((err: any) => {
              // Remove the "body" part from the location array, and join the rest
              const locPath = err.loc
                .filter((l: string) => l !== 'body')
                .join(' - ');
              return `${locPath}: ${err.msg} (received: ${err.input})`;
            })
            .join(' | ');
        } else if (data?.error_description) {
          errorMessage = data.error_description;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (data?.message) {
          errorMessage = data.message;
        }
        toast.error(errorMessage);
      }
    }
  );
};
